import React, { useEffect, useState, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { logout } from '../actions/userActions'
import { useNavigate, Link } from 'react-router-dom'
import { Dropdown } from 'react-bootstrap'
import useWebSocket from '../hooks/useWebSocket'
import { ToastContext } from '../App'
import { useTranslation } from 'react-i18next'
import DropdownComponent from './dropdown'

function Navbar({ setSidebarVisible, isSidebarVisible }) {
	const userLogin = useSelector((state) => state.userLogin)
	const { userInfo } = userLogin
	const { t, i18n } = useTranslation()

	const dispatch = useDispatch()
	const navigate = useNavigate()
	const showToast = useContext(ToastContext)

	const logoutHandler = () => {
		showToast?.('You have been logged out successfully.', 'info')
		dispatch(logout())
		navigate('/')
	}

	const handleSideToggle = () => {
		setSidebarVisible(!isSidebarVisible)
	}

	const languageOptions = [
		{ value: 'en', label: 'nav.english' },
		{ value: 'fr', label: 'nav.french' },
	]

	const [selectedLang, setSelectedLang] = useState(languageOptions[0])
	const [showNotifications, setShowNotifications] = useState(false)

	const { messages } = useWebSocket(process.env.REACT_APP_WEBSOCKET_URL)

	useEffect(() => {
		if (!userInfo) {
			navigate('/')
		}
	}, [userInfo, navigate])

	useEffect(() => {
		const savedLang = localStorage.getItem('lang')
		if (savedLang) {
			const found = languageOptions.find((opt) => opt.value === savedLang)
			if (found) {
				setSelectedLang(found)
				i18n.changeLanguage(savedLang)
			}
		}
	}, [i18n])

	const toggleNotifications = () => {
		setShowNotifications(!showNotifications)
	}

	const handleLanguageChange = (langValue) => {
		i18n.changeLanguage(langValue)

		const found = languageOptions.find((opt) => opt.value === langValue)
		setSelectedLang(found)

		localStorage.setItem('lang', langValue)
	}

	const unreadNotificationsCount = messages.length

	const formatTimestamp = (timestamp) => {
		const date = new Date(timestamp)
		return date.toLocaleString()
	}

	const getNotificationMessage = (msg) => {
		return `${msg.event_type} on object ${msg.object_id} with operation ${msg.operation} at ${formatTimestamp(msg.timestamp)}`
	}

	return (
		<div className="navbar-custom">
			<div className="topbar container-fluid">
				<div className="d-flex align-items-center gap-lg-2 gap-1">
					<div className="logo-topbar">
						<Link to="/homepage">
							<span className="logo-light">
								<span className="logo-lg">
									<h3 style={{ color: 'white' }}>KLERK</h3>
								</span>
							</span>
						</Link>
					</div>
					<i
						className="uil uil-bars side-bar-toggel"
						onClick={handleSideToggle}
						role="button"
						aria-label={t('navbar.toggleMenu')}
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								handleSideToggle()
							}
						}}
					></i>
				</div>

				<ul className="topbar-menu d-flex align-items-center gap-3">
					{/* Language Selector */}
					<li className="dropdown">
						<DropdownComponent
							title="nav.language"
							options={languageOptions}
							selected={selectedLang}
							onSelect={handleLanguageChange}
							icon={<span className="klerk-dropdown-headerIcon">🌐</span>}
							position="bottom"
						/>
					</li>

					{/* Notifications */}
					<li className="dropdown">
						<Dropdown show={showNotifications} onToggle={toggleNotifications}>
							<Dropdown.Toggle
								variant="link"
								id="notifications-dropdown"
								className="nav-link arrow-none nav-user px-2"
							>
								<i className="uil uil-bell"></i>
								{unreadNotificationsCount > 0 && (
									<span className="badge bg-danger">
										{unreadNotificationsCount}
									</span>
								)}
								<span className="d-lg-flex flex-column gap-1 d-none">
									<h5 className="my-0" style={{ color: 'white' }}>
										{t('navbar.notifications')}
									</h5>
								</span>
							</Dropdown.Toggle>

							<Dropdown.Menu
								align="end"
								className="dropdown-menu-end"
								style={{
									zIndex: 10,
									border: '1px solid grey',
									borderRadius: '15px',
									maxHeight: '300px',
									overflowY: 'auto',
								}}
							>
								{messages.length === 0 ? (
									<Dropdown.Item>{t('navbar.noNotifications')}</Dropdown.Item>
								) : (
									messages.map((msgContainer, index) =>
										msgContainer.message.map((msg, innerIndex) => (
											<Dropdown.Item key={`${index}-${innerIndex}`}>
												{getNotificationMessage(msg)}
											</Dropdown.Item>
										))
									)
								)}
							</Dropdown.Menu>
						</Dropdown>
					</li>

					{/* Cookie Settings */}
					<li className="dropdown">
						<div
							className="nav-link arrow-none nav-user px-2"
							role="button"
							onClick={() => window.Cookiebot?.renew()}
							tabIndex={0}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									window.Cookiebot?.renew()
								}
							}}
						>
							<i className="uil uil-setting"></i>
							<span className="d-lg-flex flex-column gap-1 d-none">
								<h5 className="my-0" style={{ color: 'white' }}>
									{t('navbar.cookieSettings')}
								</h5>
							</span>
						</div>
					</li>

					{/* Logout */}
					<li className="dropdown">
						<div
							className="nav-link arrow-none nav-user px-2"
							role="button"
							onClick={logoutHandler}
							tabIndex={0}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									logoutHandler()
								}
							}}
						>
							<i className="uil uil-sign-out-alt"></i>
							<span className="d-lg-flex flex-column gap-1 d-none">
								<h5 className="my-0" style={{ color: 'white' }}>
									{t('nav.logout')}
								</h5>
							</span>
						</div>
					</li>
				</ul>
			</div>

			{/* Floating Cookie Settings for Mobile */}
			<div
				className="cookie-settings-mobile"
				onClick={() => window.Cookiebot?.show()}
				role="button"
				tabIndex={0}
				aria-label={t('navbar.cookieSettings')}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						window.Cookiebot?.show()
					}
				}}
			>
				<i className="uil uil-setting"></i>
			</div>
		</div>
	)
}

export default Navbar